import { z } from "zod";

export const areaCode = z.enum([
  "UK",
  "EUROPE_HR",
  "EUROPE_LR",
  "WORLD_HR",
  "WORLD_LR",
]);

export const regionsSch = z.object({
  regionArea: z.string(),
  regions: z.string(),
  areaCode,
});

const benefitNodeSchema = z.object({
  isActive: z.boolean(),
  isMedicalExpenses: z.boolean(),
  description: z.string(),
  isShownInRenewals: z.boolean(),
  isCancellation: z.boolean(),
  isExcludeUK: z.boolean().optional(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const coverBenefitsType = z.array(benefitNodeSchema);
export type CoverBenefitsType = z.infer<typeof coverBenefitsType>;

const coverBenefitsSchema = z.object({
  defaqtoRating: z.string(),
  coverName: z.string(),
  name: z.string(),
  coverType: z.string(),
  isCruise: z.boolean().optional(),
  benefitCopy: z.record(benefitNodeSchema),
  whichBestBuyBadgeImage: z.string().optional(),
});

// Schema for coverLevelBenefits
export const coverLevelBenefitsSchema = z.record(
  z.object({
    coverBenefits: coverBenefitsSchema,
  })
);

export const policyDetailsAccordionType = z.object({
  lastYearPriceTitle: z.string(),
  documentTitle: z.string(),
  documentsSectionTitle: z.string(),
  renewalPriceClickableText: z.string(),
  renewalPriceClickableTextMobile: z.string(),
  documentsInfoText: z.string(),
  renewalPriceDrawerTitle: z.string(),
  renewalPriceDrawerContent: z.string(),
  coverLevelTitle: z.string(),
  coverLevelBenefits: coverLevelBenefitsSchema,
  moreInfoDrawerContent: z.string(),
  travelRegionTitle: z.string(),
  travelRegionEditButtonLabel: z.string(),
  cruiseCoverTitle: z.string(),
  moreInfoButtonLabel: z.string(),
  cruiseCoverEditButtonLabel: z.string(),
  policyStartDateTitle: z.string(),
  coverLevelEditButtonLabel: z.string(),
  coverLevelSectionText: z.string(),
  renewalPriceTitle: z.string(),
  moreInfoDrawerTitle: z.string(),
  policyDetailsAccordionTitle: z.string(),
  renewalPriceDiscountText: z.string(),
  regions: z.record(regionsSch),
  closeButtonDrawer: z.string(),
  infoText: z.string(),
  coverLevelComparisonSubtitle: z.string().optional(),
  coverLevelComparisonEditLinkLabel: z.string().optional(),
  coverLevelComparisonViewButtonLabel: z.string().optional(),
  coverLevelComparisonSelectedButtonLabel: z.string().optional(),
  coverLevelComparisonOrSeparator: z.string().optional(),
  coverLevelComparisonMonthlySuffix: z.string().optional(),
});

export const renewalContactDetailsAccordionType = z.object({
  contactDetailsAccordionTitle: z.string(),
  organizerDetailsTitle: z.string(),
  emailEditButtonLabel: z.string(),
  telephoneEditButtonLabel: z.string(),
  organizerNameEditButtonLabel: z.string(),
  organizerAddressEditButtonLabel: z.string(),
});

export type PolicyDetailsAccordionType = z.infer<
  typeof policyDetailsAccordionType
>;

export type RenewalContactDetailsAccordionType = z.infer<
  typeof renewalContactDetailsAccordionType
>;

export type Regions = z.infer<typeof regionsSch>;
export type CoverLevelBenefits = z.infer<typeof coverLevelBenefitsSchema>;

export const travellersAccordionType = z.object({
  title: z.string(),
  typeLabel: z.string(),
  editCtaLabel: z.string(),
  addCtaLabel: z.string(),
});

export type TravellersAccordionType = z.infer<typeof travellersAccordionType>;

export const acoId = z.enum([
  "TDE",
  "WINTER_SPORTS_COVER",
  "GOLF_EQUIPMENT",
  "CRUISE_COVER",
  "EXCESS_WAIVER",
  "HUNDRED_DAYS_DURATION",
  "GADGET_COVER",
  "CAR_HIRE_EXCESS_WAIVER",
  "PERSONAL_LIABILITY_COVER",
]);

export const acoItemSch = z.object({
  acoId, // Matching the predefined enum for acoId
  acoTitle: z.string(),
  description: z.string(),
  removeModalConfirmCTALabel: z.string(),
  removeModalCancelCTALabel: z.string(),
  removeModalTitle: z.string(),
  removeModalDescription: z.string(),
  drawerContent: z.string(),
  mgnlCreated: z.string().optional(),
  mgnlLastModified: z.string().optional(),
});

export const policyExtrasAccordionType = z.object({
  policyExtrasAccordionTitle: z.string(),
  additionalExtrasTitle: z.string(),
  includedLabel: z.string(),
  notIncludedLabel: z.string(),
  removeButtonLabel: z.string(),
  removeModalTitle: z.string(),
  removeModalContent: z.string(),
  removeModalConfirmCTALabel: z.string(),
  removeModalCancelCTALabel: z.string(),
  viewAddButtonLabel: z.string(),
  noACOsAvailableHighlightedContent: z.string(),
  seeOtherCoverLevelsLabel: z.string(),
  acoItems: z.record(
    z.object({
      acoItem: acoItemSch, // Reference to acoItem schema
    })
  ),
});

export const policyExtrasEditPageType = z.object({
  policyExtrasHeaderTitle: z.string(),
  policyExtrasHeaderDescription: z.string(),
  addButtonLabel: z.string(),
  addedButtonLabel: z.string(),
  removeButtonLabel: z.string(),
  moreInfoLabel: z.string(),
  chooseLabel: z.string(),
  backToRenewalsLabel: z.string(),
  acoItems: z.record(
    z.object({
      acoItem: acoItemSch, // Reference to acoItem schema
    })
  ),
});

export type PolicyExtrasAccordionType = z.infer<
  typeof policyExtrasAccordionType
>;
export type AcoItems = z.infer<typeof acoItemSch>;

export const medicalAccordionType = z.object({
  title: z.string(),
  description: z.string(),
  infoBoxDescription: z.string(),
  noMedicalCondition: z.string(),
  oneMedicalCondition: z.string(),
  multipleMedicalConditions: z.string(),
  addCtaLabel: z.string(),
  addEditCtaLabel: z.string(),
  travelingBefore: z.string(),
  editCtaLabel: z.string(),
});

export type MedicalAccordionType = z.infer<typeof medicalAccordionType>;
